import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { inject, observer, useLocalObservable } from 'mobx-react'

import { ISitePermitFilterInput } from '~/client/graph'

import SortOrder from '../../enums/SortOrder'
import useWorkflows from '../../hooks/useWorkflows'
import useWorkflowsByIds from '../../hooks/useWorkflowsByIds'
import Localization from '../../localization/LocalizationManager'
import UserProject from '../../models/UserProject'
import Workflow from '../../models/Workflow'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MaterialsStore from '../../stores/domain/Materials.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import ProjectsStore from '../../stores/domain/Projects.store'
import TagsStore from '../../stores/domain/Tags.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '../../stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import EventsStore from '../../stores/EventStore/Events.store'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import { Button } from '../Button/Button'
import CommonDateFilter, {
  CommonDateFilterMode,
  CommonDateFilterStore,
} from '../CommonDateFilter'
import { AddIcon } from '../icons/AddIcon'
import { ListHeader } from '../ListHeader'
import { Loader } from '../Loader'
import {
  WorkflowGroupingKey,
  WorkflowList,
  WorkflowSortingKey,
} from '../WorkflowList'
import WorkflowCreationPanel from './components/WorkflowCreationPanel/WorkflowCreationPanel'
import WorkflowFilters from './components/WorkflowFilters/WorkflowFilters'
import IWorkflowCreationParent from './IWorkflowCreationParent'
import {
  buildServerFilter,
  filterWorkflowsByUnassigned,
  FILTER_TYPE_TO_CATEGORY,
  getOnlyUnassignedFilterTypes,
  groupFiltersByCategory,
  mergeFilterArrays,
} from './utils/workflowFilterBuilder'
import {
  getWorkflowGroupingOptions,
  getWorkflowSortingOptions,
} from './WorkflowDirectory.constants'
import WorkflowDirectoryStore from './WorkflowDirectory.store'

const EMPTY_STRING_ARRAY: string[] = []

interface IProps {
  filters?: ISitePermitFilterInput
  selectedWorkflowIds?: string[]
  workflowPredicate?: (workflow: Workflow) => boolean
  onSelectionChange?: (ids: string[]) => void
  onSave?: (ids: string[]) => void
  onCancel?: () => void
  onClose?: () => void
  allowCreateNew?: boolean
  allowSelectExisting?: boolean
  initialCreating?: boolean
  allowedTypeIds?: string[]
  showQuickFilters?: boolean
  parentStore?: IWorkflowCreationParent
  isPreviewMode?: boolean
  isReadOnly?: boolean
  isMultiple?: boolean
  onWorkflowClick?: (workflow: Workflow) => void
  onBacklinksWorkflowClick?: (workflow: Workflow) => void
  restrictToIds?: string[]
  displayOnlyBacklinks?: boolean
  initialSortKey?: WorkflowSortingKey
  initialSortOrder?: SortOrder
  initialGroupByKey?: WorkflowGroupingKey | null
  initialFilterTagIds?: string[]
  initialDateFilterMode?: CommonDateFilterMode
  eventsStore?: EventsStore
  workflowTypesStore?: WorkflowTypesStore
  userProjectsStore?: UserProjectsStore
  projectDateStore?: ProjectDateStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  tagsStore?: TagsStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  projectsStore?: ProjectsStore
}

const EMPTY_FILTERS: ISitePermitFilterInput = {}

const WorkflowDirectory: React.FC<IProps> = observer(
  ({
    filters = EMPTY_FILTERS,
    selectedWorkflowIds = EMPTY_STRING_ARRAY,
    workflowPredicate,
    onSelectionChange,
    onSave,
    onCancel,
    onClose,
    allowCreateNew = true,
    allowSelectExisting = true,
    initialCreating = false,
    allowedTypeIds,
    showQuickFilters = false,
    parentStore,
    isPreviewMode = false,
    isReadOnly = false,
    isMultiple = true,
    onWorkflowClick,
    onBacklinksWorkflowClick,
    restrictToIds,
    displayOnlyBacklinks,
    initialSortKey,
    initialSortOrder,
    initialGroupByKey,
    initialFilterTagIds,
    initialDateFilterMode,
    eventsStore,
    workflowTypesStore,
    userProjectsStore,
    projectDateStore,
    tagsStore,
    projectsStore,
  }) => {
    const projectId = eventsStore?.appState?.activeProject?.id
    const currentUser = eventsStore?.appState?.user
    const currentUserCompanyId = currentUser
      ? UserProject.getCompanyId(currentUser, userProjectsStore)
      : null

    const [isCreatingNew, setIsCreatingNew] = useState(initialCreating)
    const isInitialLoadRef = useRef(true)

    const store = useLocalObservable(
      () =>
        new WorkflowDirectoryStore({
          initialSortKey,
          initialSortOrder,
          initialGroupByKey,
        }),
    )

    useEffect(() => {
      return () => {
        store.dispose()
      }
    }, [store])

    const dateSelectorStore = useMemo(
      () =>
        new CommonDateFilterStore(
          projectDateStore,
          undefined,
          initialDateFilterMode,
          projectsStore,
        ),
      [projectDateStore, projectsStore, initialDateFilterMode],
    )

    const { workflows: selectedWorkflows } = useWorkflowsByIds(
      projectId,
      selectedWorkflowIds,
    )

    const dateAnchorAppliedRef = useRef(false)
    useEffect(() => {
      if (dateAnchorAppliedRef.current || !selectedWorkflowIds.length) return
      const target = selectedWorkflows.find(w =>
        selectedWorkflowIds.includes(w.id),
      )
      if (!target?.startDate) return
      dateSelectorStore.handleDateSelect(new Date(target.startDate))
      dateSelectorStore.applyPendingSelection()
      dateAnchorAppliedRef.current = true
    }, [selectedWorkflows])

    const [selectedFilterTagIds, setSelectedFilterTagIds] = useState<string[]>(
      initialFilterTagIds ?? [],
    )

    const groupingOptions = useMemo(() => getWorkflowGroupingOptions(), [])
    const sortingOptions = useMemo(() => getWorkflowSortingOptions(), [])

    const projectIdSet = useMemo(
      () => new Set(projectsStore?.list.map(p => p.id) || []),
      [projectsStore?.list],
    )

    const filtersByCategory = useMemo(
      () =>
        groupFiltersByCategory(
          selectedFilterTagIds,
          tagsStore,
          projectIdSet,
          FILTER_TYPE_TO_CATEGORY,
        ),
      [selectedFilterTagIds, tagsStore, projectIdSet],
    )

    const onlyUnassignedFilterTypes = useMemo(
      () =>
        getOnlyUnassignedFilterTypes(
          filtersByCategory,
          FILTER_TYPE_TO_CATEGORY,
        ),
      [filtersByCategory],
    )

    const tagBasedServerFilter = useMemo(
      () => buildServerFilter(filtersByCategory, workflowTypesStore),
      [filtersByCategory, workflowTypesStore],
    )

    const quickFilterFilters = useMemo(() => {
      const baseFilters: ISitePermitFilterInput = {
        ...filters,
        ...tagBasedServerFilter,
      }

      if (restrictToIds?.length) {
        baseFilters.ids = restrictToIds
      }

      if (store.isMyWorkflowsActive && currentUser?.id) {
        baseFilters.assigneeIds = mergeFilterArrays(
          baseFilters.assigneeIds,
          currentUser.id,
        )
      }

      if (store.isMyCompanyActive && currentUserCompanyId) {
        baseFilters.companyIds = mergeFilterArrays(
          baseFilters.companyIds,
          currentUserCompanyId,
        )
      }

      if (store.debouncedSearchKey) {
        baseFilters.searchString = store.debouncedSearchKey
      }
      if (dateSelectorStore.filters.isDateFilterActive) {
        baseFilters.startDate = dateSelectorStore.filters.startDate.getTime()
        baseFilters.endDate = dateSelectorStore.endDate.getTime()
      }
      return baseFilters
    }, [
      filters,
      tagBasedServerFilter,
      restrictToIds,
      store.isMyWorkflowsActive,
      store.isMyCompanyActive,
      store.debouncedSearchKey,
      currentUser?.id,
      currentUserCompanyId,
      dateSelectorStore.filters.isDateFilterActive,
      dateSelectorStore.filters.startDate,
      dateSelectorStore.endDate,
    ])

    const { workflows: allWorkflows, workflowsLoading } = useWorkflows(
      projectId,
      currentUser?.id,
      quickFilterFilters,
      undefined,
      !projectId,
    )

    useEffect(() => {
      if (!workflowsLoading && allWorkflows.length > 0) {
        isInitialLoadRef.current = false
      }
    }, [workflowsLoading, allWorkflows.length])

    const workflows = useMemo(() => {
      let result = allWorkflows

      if (restrictToIds?.length) {
        const idSet = new Set(restrictToIds)
        result = result.filter(w => idSet.has(w.id))
      }

      if (workflowPredicate) {
        result = result.filter(workflowPredicate)
      }

      return result
    }, [allWorkflows, workflowPredicate, restrictToIds])

    const filteredWorkflows = useMemo(
      () => filterWorkflowsByUnassigned(workflows, onlyUnassignedFilterTypes),
      [workflows, onlyUnassignedFilterTypes],
    )

    const availableProjectIds = useMemo(() => {
      const projectIds = new Set<string>()
      if (projectId) {
        projectIds.add(projectId)
      }
      allWorkflows.forEach(workflow => {
        if (workflow.projectId) {
          projectIds.add(workflow.projectId)
        }
      })
      return Array.from(projectIds)
    }, [allWorkflows, projectId])

    const handleGroupingChange = useCallback(
      (key: string | null) => {
        store.setGroupingKey(key as WorkflowGroupingKey | null)
      },
      [store],
    )

    const [currentSelectedIds, setCurrentSelectedIds] =
      useState<string[]>(selectedWorkflowIds)

    useEffect(() => {
      setCurrentSelectedIds(selectedWorkflowIds)
    }, [selectedWorkflowIds])

    const hasPendingChanges = useMemo(() => {
      if (currentSelectedIds.length !== selectedWorkflowIds.length) return true
      const initialSet = new Set(selectedWorkflowIds)
      return currentSelectedIds.some(id => !initialSet.has(id))
    }, [currentSelectedIds, selectedWorkflowIds])

    const handleSelectionChange = useCallback(
      (ids: string[]) => {
        setCurrentSelectedIds(ids)
        onSelectionChange?.(ids)
      },
      [onSelectionChange],
    )

    const handleApply = useCallback(() => {
      onSave?.(currentSelectedIds)
    }, [currentSelectedIds, onSave])

    const handleCancel = useCallback(() => {
      setCurrentSelectedIds(selectedWorkflowIds)
      onCancel?.()
    }, [selectedWorkflowIds, onCancel])

    const handleCreateNew = useCallback(() => {
      if (!parentStore) return
      setIsCreatingNew(true)
    }, [parentStore])

    const handleBackToList = useCallback(() => {
      setIsCreatingNew(false)
    }, [])

    const handleSingleSelect = useCallback(
      (workflow: Workflow) => {
        const isAlreadySelected = currentSelectedIds.includes(workflow.id)
        onSave?.(isAlreadySelected ? [] : [workflow.id])
      },
      [onSave, currentSelectedIds],
    )

    useEffect(() => {
      if (
        allowCreateNew &&
        !allowSelectExisting &&
        parentStore &&
        !isCreatingNew
      ) {
        handleCreateNew()
      }
    }, [
      allowCreateNew,
      allowSelectExisting,
      parentStore,
      handleCreateNew,
      isCreatingNew,
    ])

    const onApplyTagFilters = useCallback((tagIds: string[]) => {
      setSelectedFilterTagIds(tagIds)
    }, [])

    if (isCreatingNew && parentStore) {
      return (
        <WorkflowCreationPanel
          parentStore={parentStore}
          allowedTypeIds={allowedTypeIds}
          isPreviewMode={isPreviewMode}
          allowSelectExisting={allowSelectExisting}
          onBackToList={handleBackToList}
          onCancel={onClose}
        />
      )
    }

    if (workflowsLoading && isInitialLoadRef.current) {
      return (
        <div className="workflow-directory col">
          <Loader />
        </div>
      )
    }

    return (
      <div className="workflow-directory col overflow-hidden">
        {/* Date Selector Row */}
        <div className="tw:flex tw:items-center tw:px-2.5 tw:py-1">
          <CommonDateFilter
            store={dateSelectorStore}
            projectDateStore={projectDateStore}
          />
        </div>

        <ListHeader.DisplayBar
          search={{
            value: store.searchKey,
            onChange: store.setSearch,
          }}
          quickFilters={
            showQuickFilters
              ? {
                  isOnlyMe: store.isMyWorkflowsActive,
                  isOnlyMyCompany: store.isMyCompanyActive,
                  onToggleMe: store.toggleMyWorkflows,
                  onToggleMyCompany: store.toggleMyCompany,
                }
              : undefined
          }
          filter={
            <WorkflowFilters
              onApplyFilters={onApplyTagFilters}
              initialSelectedTagIds={selectedFilterTagIds}
              availableProjectIds={availableProjectIds}
            />
          }
          grouping={{
            value: store.groupingKey,
            defaultValue: initialGroupByKey ?? null,
            options: groupingOptions,
            onChange: handleGroupingChange,
          }}
          // No key is the store's natural order; DESC is its default direction.
          sorting={{
            value: { key: store.sortingKey, order: store.sortOrder },
            defaultValue: {
              key: initialSortKey ?? null,
              order: initialSortOrder ?? SortOrder.DESC,
            },
            options: sortingOptions,
            onChange: value =>
              store.setSort(
                value.key as WorkflowSortingKey | null,
                value.order,
              ),
          }}
        />

        <WorkflowList
          variant="menu"
          workflows={filteredWorkflows}
          loading={workflowsLoading}
          groupingKey={store.groupingKey}
          sortKey={store.sortingKey}
          sortOrder={store.sortOrder}
          isReadOnly={isReadOnly}
          isMultiple={isMultiple}
          selectedIds={currentSelectedIds}
          onSelectionChange={handleSelectionChange}
          onWorkflowClick={onWorkflowClick}
          onSingleSelect={!isMultiple ? handleSingleSelect : undefined}
          displayOnlyBacklinks={displayOnlyBacklinks}
          onBacklinksWorkflowClick={onBacklinksWorkflowClick}
        />

        {/* Footer with Create/Save/Cancel buttons - hidden in read-only and single-select-only mode */}
        {!isReadOnly && (isMultiple || (allowCreateNew && parentStore)) && (
          <div className="tw:flex tw:items-center tw:justify-between tw:px-2.5 tw:pt-2.5 tw:pb-safe-3 tw:border-0 tw:border-t tw:border-solid tw:border-input-soft">
            {allowCreateNew && parentStore && (
              <Button variant="outline" onClick={handleCreateNew} size="lg">
                <AddIcon data-icon="inline-start" size="xs" />
                {Localization.translator.createNew}
              </Button>
            )}
            {isMultiple && (
              <>
                {(!allowCreateNew || !parentStore) && <div />}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Button variant="outline" onClick={handleCancel} size="lg">
                    {Localization.translator.cancel}
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleApply}
                    disabled={!hasPendingChanges}
                    size="lg"
                  >
                    {Localization.translator.apply}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    )
  },
)

export default inject(
  'eventsStore',
  'workflowTypesStore',
  'userProjectsStore',
  'projectDateStore',
  'projectMembersStore',
  'locationAttributesStore',
  'materialsStore',
  'tagsStore',
  'workflowCategoriesStore',
  'projectsStore',
)(WorkflowDirectory)
